import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  StudioRole,
  ModulePermissionGroup,
  EffectivePermissions,
} from "../../../Data/role.dto";
import {
  FALLBACK_PERMISSIONS_CATALOG,
  normalizeCatalog,
} from "../../../Data/permissionsCatalog";
import {
  getPermissionsCatalogAPI,
  getAllRolesAPI,
  getRoleByIdAPI,
  createRoleAPI,
  updateRoleAPI,
  deleteRoleAPI,
  assignRoleAPI,
  getMyPermissionsAPI,
} from "../../ApiCalls/Dashboard/RoleApi";
import { verifyOTP, getUserByToken } from "../../ApiCalls/Auth/login";
import { clearToken } from "../Auth/AuthSlice";
import {
  getStoredStudioAccess,
  setStoredStudioAccess,
  clearStoredStudioAccess,
} from "../../../Utils/permissionUtils";

export interface RoleLoadingState {
  roles: boolean;
  catalog: boolean;
  permissions: boolean;
  assign: boolean;
  save: boolean;
  delete: boolean;
}

export interface RoleState {
  roles: StudioRole[];
  currentRole: StudioRole | null;
  catalog: ModulePermissionGroup[];
  myPermissions: EffectivePermissions | null;
  loading: RoleLoadingState;
  error: string | null;
  success: boolean;
  isRolesFetched: boolean;
}

const initialState: RoleState = {
  roles: [],
  currentRole: null,
  catalog: FALLBACK_PERMISSIONS_CATALOG,
  myPermissions: getStoredStudioAccess(),
  loading: {
    roles: false,
    catalog: false,
    permissions: false,
    assign: false,
    save: false,
    delete: false,
  },
  error: null,
  success: false,
  isRolesFetched: false,
};

const roleSlice = createSlice({
  name: "role",
  initialState,
  reducers: {
    setCurrentRole(state, action: PayloadAction<StudioRole | null>) {
      state.currentRole = action.payload;
    },
    clearRoleError(state) {
      state.error = null;
    },
    clearRoleSuccess(state) {
      state.success = false;
    },
    setMyPermissions(state, action: PayloadAction<EffectivePermissions | null>) {
      state.myPermissions = action.payload;
      state.loading.permissions = false;
      setStoredStudioAccess(action.payload);
    },
    resetRoleState: (state) => {
      clearStoredStudioAccess();
      return {
        ...initialState,
        myPermissions: null,
      };
    },
  },
  extraReducers: (builder) => {
    // 1. Catalog
    builder
      .addCase(getPermissionsCatalogAPI.pending, (state) => {
        state.loading.catalog = true;
      })
      .addCase(getPermissionsCatalogAPI.fulfilled, (state, action) => {
        state.loading.catalog = false;
        state.catalog = normalizeCatalog(action.payload);
      })
      .addCase(getPermissionsCatalogAPI.rejected, (state, action: any) => {
        state.loading.catalog = false;
        // Keep fallback catalog so UI remains functional
      });

    // 2. All Roles
    builder
      .addCase(getAllRolesAPI.pending, (state) => {
        state.loading.roles = true;
        state.error = null;
      })
      .addCase(getAllRolesAPI.fulfilled, (state, action) => {
        state.loading.roles = false;
        state.isRolesFetched = true;
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.roles = payload;
        } else if (payload && Array.isArray(payload.data)) {
          state.roles = payload.data;
        } else if (payload && Array.isArray(payload.roles)) {
          state.roles = payload.roles;
        } else {
          state.roles = [];
        }
      })
      .addCase(getAllRolesAPI.rejected, (state, action: any) => {
        state.loading.roles = false;
        state.error = action.payload?.message || "Failed to fetch roles";
      });

    // 3. Single Role
    builder
      .addCase(getRoleByIdAPI.pending, (state) => {
        state.loading.roles = true;
      })
      .addCase(getRoleByIdAPI.fulfilled, (state, action) => {
        state.loading.roles = false;
        state.currentRole = action.payload?.data || action.payload;
      })
      .addCase(getRoleByIdAPI.rejected, (state, action: any) => {
        state.loading.roles = false;
        state.error = action.payload?.message || "Failed to fetch role";
      });

    // 4. Create Role
    builder
      .addCase(createRoleAPI.pending, (state) => {
        state.loading.save = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createRoleAPI.fulfilled, (state, action) => {
        state.loading.save = false;
        state.success = true;
        const newRole = action.payload?.data || action.payload;
        if (newRole && newRole.id) {
          state.roles.unshift(newRole);
        }
      })
      .addCase(createRoleAPI.rejected, (state, action: any) => {
        state.loading.save = false;
        state.error = action.payload?.message || "Failed to create role";
      });

    // 5. Update Role
    builder
      .addCase(updateRoleAPI.pending, (state) => {
        state.loading.save = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateRoleAPI.fulfilled, (state, action) => {
        state.loading.save = false;
        state.success = true;
        const updatedRole = action.payload?.data || action.payload;
        if (updatedRole && updatedRole.id) {
          state.roles = state.roles.map((r) =>
            r.id === updatedRole.id ? { ...r, ...updatedRole } : r
          );
          if (state.currentRole?.id === updatedRole.id) {
            state.currentRole = updatedRole;
          }
        }
      })
      .addCase(updateRoleAPI.rejected, (state, action: any) => {
        state.loading.save = false;
        state.error = action.payload?.message || "Failed to update role";
      });

    // 6. Delete Role
    builder
      .addCase(deleteRoleAPI.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteRoleAPI.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.roles = state.roles.filter((r) => r.id !== action.payload.id);
        if (state.currentRole?.id === action.payload.id) {
          state.currentRole = null;
        }
      })
      .addCase(deleteRoleAPI.rejected, (state, action: any) => {
        state.loading.delete = false;
        state.error = action.payload?.message || "Failed to delete role";
      });

    // 7. Assign Role
    builder
      .addCase(assignRoleAPI.pending, (state) => {
        state.loading.assign = true;
        state.error = null;
      })
      .addCase(assignRoleAPI.fulfilled, (state, action) => {
        state.loading.assign = false;
        state.success = true;
      })
      .addCase(assignRoleAPI.rejected, (state, action: any) => {
        state.loading.assign = false;
        state.error = action.payload?.message || "Failed to assign role";
      });

    // 8. My Permissions
    builder
      .addCase(getMyPermissionsAPI.pending, (state) => {
        state.loading.permissions = true;
      })
      .addCase(getMyPermissionsAPI.fulfilled, (state, action) => {
        state.loading.permissions = false;
        const raw = action.payload?.data || action.payload;
        if (raw) {
          state.myPermissions = {
            is_studio_owner: Boolean(raw.is_studio_owner),
            studio_role: raw.studio_role || null,
            permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
            modules: Array.isArray(raw.modules) ? raw.modules : [],
            all_modules: Array.isArray(raw.all_modules) ? raw.all_modules : [],
          };
          setStoredStudioAccess(state.myPermissions);
        }
      })
      .addCase(getMyPermissionsAPI.rejected, (state, action: any) => {
        state.loading.permissions = false;
      });

    // 9. Immediate Permission Hydration from Login
    builder.addCase(verifyOTP.fulfilled, (state, action: any) => {
      const studioAccess = action.payload?.studio_access || action.payload?.data?.studio_access;
      if (studioAccess) {
        state.myPermissions = {
          is_studio_owner: Boolean(studioAccess.is_studio_owner),
          studio_role: studioAccess.studio_role || null,
          permissions: Array.isArray(studioAccess.permissions) ? studioAccess.permissions : [],
          modules: Array.isArray(studioAccess.modules) ? studioAccess.modules : [],
          all_modules: Array.isArray(studioAccess.all_modules) ? studioAccess.all_modules : [],
        };
        state.loading.permissions = false;
        setStoredStudioAccess(state.myPermissions);
      }
    });

    // 10. Immediate Permission Hydration from Token Revalidation
    builder.addCase(getUserByToken.fulfilled, (state, action: any) => {
      const studioAccess = action.payload?.studio_access || action.payload?.data?.studio_access;
      if (studioAccess) {
        state.myPermissions = {
          is_studio_owner: Boolean(studioAccess.is_studio_owner),
          studio_role: studioAccess.studio_role || null,
          permissions: Array.isArray(studioAccess.permissions) ? studioAccess.permissions : [],
          modules: Array.isArray(studioAccess.modules) ? studioAccess.modules : [],
          all_modules: Array.isArray(studioAccess.all_modules) ? studioAccess.all_modules : [],
        };
        state.loading.permissions = false;
        setStoredStudioAccess(state.myPermissions);
      }
    });

    // 11. Clear role state on logout
    builder.addCase(clearToken, () => {
      clearStoredStudioAccess();
      return {
        ...initialState,
        myPermissions: null,
      };
    });
  },
});

export const {
  setCurrentRole,
  clearRoleError,
  clearRoleSuccess,
  setMyPermissions,
  resetRoleState,
} = roleSlice.actions;

export default roleSlice.reducer;
